import { fromJS } from 'immutable';
import Customer from '../models/Customer';
import CustomerConstants from '../constants/CustomerConstants';
import CustomerStore from '../stores/CustomerStore';
import ShoppingCartActions from './ShoppingCartActions';

const isBrowser = (typeof window !== 'undefined');

let CustomerActions = {
  auth: (context, payload) => {
    context.dispatch(CustomerConstants.AUTH_CUSTOMER_START, {});

    let addIdentifierIfNotFound = payload.addIdentifierIfNotFound || false;
    let shoppingCartFetchPayload = payload.shoppingCartFetchPayload || {};
    delete payload.addIdentifierIfNotFound;
    delete payload.shoppingCartFetchPayload;

    let handleSuccessResponse = (responsePayload) => {
      let customer = new Customer(responsePayload.customer);
      context.dispatch(CustomerConstants.AUTH_CUSTOMER_SUCCESS, {customer});

      if(responsePayload.session_key && responsePayload.session_token) {
        context.delectoryApi.setAuthCredentials(responsePayload);
      }

      context.identify(customer, true);
      context.executeAction(ShoppingCartActions.fetch, shoppingCartFetchPayload);

      return customer;
    };

    // Inject environment hash
    payload.environment = context.getEnvironmentProps();

    return context
      .delectoryApi
      .customers
      .auth(payload)
      .then(handleSuccessResponse)
      .catch(err => {
        if(err.name === 'NotFoundError') {
          if(addIdentifierIfNotFound && payload.hasOwnProperty('email')) {
            context
              .delectoryApi
              .customers
              .addIdentifier({type: 'email_identifier', string: payload.email})
              .then(handleSuccessResponse)
              .catch(err => {
                context.dispatch(CustomerConstants.AUTH_CUSTOMER_FAILURE, err);
              });
          } else {
            context.dispatch(CustomerConstants.AUTH_CUSTOMER_FAILURE, null);
          }
        } else {
          context.dispatch(CustomerConstants.AUTH_CUSTOMER_FAILURE, err);
        }
      });
  },

  dismissCookies: (context, payload) => {
    context.dispatch(CustomerConstants.DISMISS_COOKIES_START, {});
    context.delectoryApi.setCookiesDismissed({dismiss_cookies: payload.dismiss_cookies});
  },

  loadDismissCookies: (context, payload) => {
    context.dispatch(CustomerConstants.LOAD_DISMISS_COOKIES_START, {dismiss_cookies: context.delectoryApi._dismiss_cookies});
  },

  signOut: (context, payload) => {
    context.dispatch(CustomerConstants.SIGNOUT_CUSTOMER_START, {});

    // TODO: API to expire session

    let customer = context.getStore(CustomerStore).getCurrentCustomer();
    // Create new customer with same zip (if present)
    let authPayload = {};
    if(customer) {
      authPayload.preferences = [
        {key: 'zip_code', value: customer.preferences.zip_code}
      ];
    }
    return CustomerActions.auth(context, authPayload);
  },

  findOrCreate: (context, payload) => {
    let { session_key, session_token } = context.delectoryApi.getAuthCredentials();
    if(session_key && session_token) {
      context.dispatch(CustomerConstants.FETCH_CUSTOMER_START, {});
      return context
        .delectoryApi
        .customers
        .me()
        .then(responsePayload => {
          let customer = new Customer(responsePayload.customer);
          context.dispatch(CustomerConstants.FETCH_CUSTOMER_SUCCESS, customer);
          context.identify(customer);
          return customer;
        })
        .catch(err => {
          context.dispatch(CustomerConstants.FETCH_CUSTOMER_FAILURE, err);
        });
    }

    return CustomerActions.auth(context, {});
  },

  addIdentifier: (context, payload) => {
    context.dispatch(CustomerConstants.ADD_CUSTOMER_IDENTIFIER_START, {});

    return context.delectoryApi
      .customers
      .addIdentifier(payload)
      .then(responsePayload => {
        let customer = new Customer(responsePayload.customer);
        context.dispatch(CustomerConstants.ADD_CUSTOMER_IDENTIFIER_SUCCESS, customer);
        return customer;
      })
      .catch(err => {
        context.dispatch(CustomerConstants.ADD_CUSTOMER_IDENTIFIER_FAILURE, err);
      });
  },

  resetPassword: (context, payload) => {
    context.dispatch(CustomerConstants.RESET_CUSTOMER_PASSWORD_START, {});

    return context.delectoryApi
      .customers
      .resetPassword(payload)
      .then(responsePayload => {
        context.dispatch(CustomerConstants.RESET_CUSTOMER_PASSWORD_SUCCESS, {});
      })
      .catch(err => {
        context.dispatch(CustomerConstants.RESET_CUSTOMER_PASSWORD_FAILURE, err);
      });
  },

  addShippingAddress: (context, payload) => {
    let addShippingAddressPayload = {
      address: payload.address
    };
    return genericUpdate(context, 'addShippingAddress', payload)
      .then((customer) => {
        if(!customer || !payload.store_id) {return null;}
        let setShippingAddressPayload = {
          store_id: payload.store_id,
          shipping_address_id: customer.shipping_addresses.get(0).id // Use id of first
        };
        return ShoppingCartActions.setShippingAddress(context, setShippingAddressPayload);
      });
  },

  updateShippingAddress: (context, payload) => {
    return genericUpdate(context, 'updateShippingAddress', payload);
  },

  removeShippingAddress: (context, payload) => {
    return genericUpdate(context, 'removeShippingAddress', payload);
  },

  addPaymentMethod: (context, payload) => {
    let addPaymentMethodPayload = {
      payment_method: payload.payment_method
    };
    return genericUpdate(context, 'addPaymentMethod', payload)
      .then((customer) => {
        if(!customer) {return null;}
        if(!payload.store_id) {return customer;}
        let setPaymentMethodPayload = {
          store_id: payload.store_id,
          payment_method_id: customer.payment_methods.get(0).id
        };
        return ShoppingCartActions.setPaymentMethod(context, setPaymentMethodPayload);
      });
  },

  removePaymentMethod: (context, payload) => {
    return genericUpdate(context, 'removePaymentMethod', payload);
  },

  updatePreferences: (context, payload) => {
    return genericUpdate(context, 'updatePreferences', payload);
  },

  updateNotificationSettings: (context, payload) => {
    return genericUpdate(context, 'updateNotificationSettings', payload);
  },

  updateContactInfo: (context, payload) => {
    return genericUpdate(context, 'updateContactInfo', payload);
  },

  updatePassword: (context, payload) => {
    return genericUpdate(context, 'updatePassword', payload);
  }
};

export default CustomerActions;

function genericUpdate(context, action, payload) {
  context.dispatch(CustomerConstants.UPDATE_CUSTOMER_START, {});
  return context
    .delectoryApi
    .customers[action](payload)
    .then(responsePayload => {
      let customer = new Customer(responsePayload.customer);
      context.dispatch(CustomerConstants.UPDATE_CUSTOMER_SUCCESS, customer);
      return customer;
    })
    .catch(err => {
      context.dispatch(CustomerConstants.UPDATE_CUSTOMER_FAILURE, err);
    });
}



// WEBPACK FOOTER //
// banquet/actions/CustomerActions.js